import { Link } from "react-router-dom";
import { BrandLogo } from "./Logo";
import { SocialLinks } from "./SocialLinks";
import { DirectorsNote } from "./DirectorsNote";
import { BrochureLink } from "./BrochureLink";
import {
  CONTACT_EMAIL,
  CONTACT_PHONE_DISPLAY,
  CONTACT_PHONE_TEL,
  CALENDLY_URL,
  CORPORATE,
  GOOGLE_REVIEW_WRITE_URL,
} from "../data/content";

const SERVICE_LINKS = [
  { to: "/suburbiq", label: "SuburbIQ" },
  { to: "/property-portfolio-assessment", label: "Property & Portfolio Assessment" },
  { to: "/buyer-advocacy", label: "Buyer Advocacy" },
  { to: "/vendor-advocacy", label: "Vendor Advocacy" },
];

const EXPLORE_LINKS = [
  { to: "/how-we-work", label: "How We Work" },
  { to: "/about", label: "About" },
  { to: "/insights", label: "Insights" },
  { to: "/faqs", label: "FAQs" },
  { to: "/fees", label: "Professional Fees" },
  { to: "/contact", label: "Contact" },
];

export const Footer = () => (
  <footer className="bg-charcoal text-ivory border-t border-white/10" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12">
      <div className="lg:col-span-4">
        <Link to="/" aria-label="80 Metrics home" className="inline-block py-2">
          <BrandLogo className="h-28 lg:h-32 w-auto" />
        </Link>
        <p className="mt-6 text-sm leading-relaxed text-ivory/60 max-w-xs">
          Independent advice to understand your options. Advocacy when you choose to act.
        </p>
        <p className="mt-6 text-xs text-ivory/40 leading-relaxed" data-testid="footer-corporate">
          {CORPORATE.trading_line}
          <br />
          {CORPORATE.abn} · {CORPORATE.licence}
          <br />
          {CORPORATE.address}
        </p>
        <div className="mt-8" data-testid="footer-contact">
          <p className="text-xs font-semibold tracking-[0.28em] uppercase text-goldlight">Talk to us</p>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            data-testid="footer-email-link"
            className="mt-4 block text-sm text-ivory/80 hover:text-goldlight transition-colors duration-300"
          >
            {CONTACT_EMAIL}
          </a>
          <a
            href={`tel:${CONTACT_PHONE_TEL}`}
            data-testid="footer-phone-link"
            className="mt-2 block text-sm text-ivory/80 hover:text-goldlight transition-colors duration-300"
          >
            {CONTACT_PHONE_DISPLAY}
          </a>
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-booking-link"
            className="mt-3 block text-sm text-ivory/80 hover:text-goldlight transition-colors duration-300"
          >
            Book a 15-minute introductory call ↗
          </a>
          <SocialLinks dark className="mt-5" testidPrefix="footer-social" />
        </div>
      </div>
      <div className="lg:col-span-2">
        <p className="text-xs font-semibold tracking-[0.28em] uppercase text-goldlight">Services</p>
        <ul className="mt-5 space-y-3">
          {SERVICE_LINKS.map((l) => (
            <li key={l.to}>
              <Link to={l.to} className="text-sm text-ivory/70 hover:text-goldlight transition-colors duration-300">
                {l.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <div className="lg:col-span-2">
        <p className="text-xs font-semibold tracking-[0.28em] uppercase text-goldlight">Explore</p>
        <ul className="mt-5 space-y-3">
          {EXPLORE_LINKS.map((l) => (
            <li key={l.to}>
              <Link to={l.to} className="text-sm text-ivory/70 hover:text-goldlight transition-colors duration-300">
                {l.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <div className="lg:col-span-4">
        <DirectorsNote dark />
      </div>
    </div>
    <div className="border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-6 pb-24 sm:pb-6 flex flex-col sm:flex-row justify-between gap-3 text-xs text-ivory/40">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <span>© {new Date().getFullYear()} 80 Metrics Property Advisory. All rights reserved.</span>
          <span>Evidence over opinion.</span>
        </div>
        {/* Right padding reserves the floating concierge launcher's safe zone
            so it can never cover these links when the footer is in view. */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 sm:pr-44 lg:pr-52">
          <Link to="/privacy-policy" data-testid="footer-privacy-link" className="hover:text-goldlight transition-colors duration-300">
            Privacy Policy
          </Link>
          <Link to="/unsubscribe" data-testid="footer-unsubscribe-link" className="hover:text-goldlight transition-colors duration-300">
            Unsubscribe
          </Link>
          <BrochureLink dark />
          <a
            href={GOOGLE_REVIEW_WRITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-leave-review-link"
            className="hover:text-goldlight transition-colors duration-300"
          >
            Leave a Google review
          </a>
        </div>
      </div>
    </div>
  </footer>
);
